import * as Sentry from '@sentry/react';
import { encryptToken } from '../helper/helper';
import { getAuth } from 'firebase/auth';
import { getAuthInstance } from '../firebase';
import { Toast } from '../components/Common/Toast';
import { TokenCookieManager } from '../utils/tokenCookies';
import { getImpersonationToken, shouldUseImpersonationAuth } from '../utils/impersonation.utils';
import { writeAuthToken } from '../utils/authToken';

export interface IRequestHeaders {
  'Content-Type': string;
  Authorization?: string; // Optional since it depends on `token`
  access_key?: string | null; // Optional since it depends on `access_key` (for demo version)
  subscribe_plan_id?: string | null; // Optional since it depends on `access_key` (for auto subscription)
  invite_access_token?: string | null; // Optional since it depends on `invite_access_token` (for invite user)
  share?: string | null; // Optional since it depends on `share` (for share book)
  returnStats?: boolean; // Optional since it depends on `returnStats` (for return stats)
}

interface IRequestMeta {
  headers: Partial<IRequestHeaders>;
  method?: string;
  body?: string;
  keepalive?: boolean;
  credentials?: RequestCredentials;
}

const callAPI = async <T>(
  endPoint: string,
  data: T | null = null,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  headers: Partial<IRequestHeaders> = {},
  opts?: {
    returnRawResponse?: boolean;
    isAbsoluteURL?: boolean;
    keepalive?: boolean;
  },
): Promise<any> => {
  try {
    const isImpersonationAuth = shouldUseImpersonationAuth();

    let postRequestMeta: IRequestMeta = {
      headers: { 'Content-Type': 'application/json', ...headers },
      ...(!isImpersonationAuth ? { credentials: 'include' } : {}), // Always include cookies
      ...(!!opts?.keepalive ? { keepalive: true } : {}),
    };

    if (data || (method !== 'GET' && method !== 'POST')) {
      postRequestMeta = {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body: data ? JSON.stringify(data) : undefined,
        ...(!isImpersonationAuth ? { credentials: 'include' } : {}), // Always include cookies
      };
    }

    // Check for impersonation token first, then fallback to regular token
    if (isImpersonationAuth) {
      const impersonationToken = getImpersonationToken();
      if (impersonationToken) {
        postRequestMeta.headers.Authorization = `Bearer ${impersonationToken}`;
      }
    } else {
      // Use regular token flow (commented out in original code)
      // const token = TokenCookieManager.getToken();
      // if (token) postRequestMeta.headers.Authorization = `Bearer ${token}`;
    }

    // @ts-ignore
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s client-side timeout
    // @ts-ignore
    const response = await fetch(`${process.env.REACT_APP_API_URL}/${endPoint}`, {
      ...postRequestMeta,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (opts?.returnRawResponse) {
      return response;
    }

    if (!response.ok) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: response.status >= 500 ? 'error' : 'warning',
        message: `${method} ${endPoint} → ${response.status}`,
        data: {
          status: response.status,
          statusText: response.statusText,
          endpoint: endPoint,
          method,
          retry_after: response.headers.get('retry-after') || undefined,
        },
      });
      console.warn(`[callAPI] ${method} ${endPoint} → ${response.status} ${response.statusText}`);
    }

    // Parse the body for both 2xx and non-2xx so callers can branch on shape
    // (e.g., 409 with requireConfirmation, 403 with low-credit error). When the
    // body isn't JSON (infra-level 502/504 HTML pages), synthesize a minimal
    // object so callers don't crash on data.status.
    const responseData = response.ok
      ? await response.json()
      : await response.json().catch(() => ({ status: response.status, error: response.statusText }));
    if (responseData?.error === 'Token Expired') {
      if (!shouldUseImpersonationAuth()) {
        TokenCookieManager.removeToken();
      }
      await reloadToken();
      return await callAPI(endPoint, data, method, headers, opts);
    }

    return responseData;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      console.warn(`[callAPI] ${endPoint} aborted (timeout)`);
      return null;
    }
    if (err?.message && /failed to fetch|networkerror|load failed/i.test(err.message)) {
      console.warn(`[callAPI] ${endPoint} network error:`, err.message);
      return null;
    }
    Sentry.captureException(err, {
      tags: { api_endpoint: endPoint, api_method: method },
      fingerprint: ['callAPI', endPoint, err?.name || 'unknown'],
    });
    console.error(`[callAPI] ${endPoint} failed:`, err?.name, err?.message);
    return null;
  }
};

export async function reloadToken(): Promise<void> {
  const currentAuth = getAuthInstance();
  const currentUser = currentAuth.currentUser;
  if (currentUser) {
    const newIdToken = await currentUser.getIdToken(true);
    const encryptedToken = encryptToken(newIdToken, process.env.REACT_APP_TOKEN_ENCRYPT_KEY || '');
    writeAuthToken(encryptedToken);
  } else {
    window.location.reload();
  }
}

export async function handleResponseDownloadFile(response: Response, filename: string = 'download.pdf'): Promise<void> {
  const blob = await response.blob();

  const contentDisposition = response.headers.get('Content-Disposition');

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const downloadPDFfile = async (url: string): Promise<void> => {
  try {
    const response = await fetch(url);
    await handleResponseDownloadFile(response);
  } catch (error) {
    Toast('Error download PDF file', true);
  }
};

export { callAPI };
